import { useClient } from '@cortex/react-query';
import type {
  ModelConfig,
  OrchestrationGroup,
  PersistentAgentGroup,
  PersistentAgentPeerGrantInput,
} from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type CSSProperties } from 'react';

import { HandlebarsInstructionsEditor } from '../../components/agents/HandlebarsInstructionsEditor';
import { BackButton } from '../../components/common/BackButton';
import { InfoHint } from '../../components/common/InfoHint';
import { PageHeader } from '../../components/common/PageHeader';
import {
  PeerGrantsEditor,
  fromPeerGrantDraft,
  type IPeerGrantDraft,
} from '../../components/persistent-agents/PeerGrantsEditor';
import { routes } from '../../lib/constants';
import { preferOpenAiModelId, sortModelsOpenAiFirst } from '../../lib/prefer-openai-model';

import { parseAgentsNewSearch } from './-new-search';

export const Route = createFileRoute('/agents/new')({
  validateSearch: parseAgentsNewSearch,
  component: NewAgentPage,
});

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };
const grid2: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--sp-3)',
};

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

type RequiredFactRow = { laneId: string; type: 'text' };

function NewAgentPage() {
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();
  const search = Route.useSearch();

  // Agents are only ever ORCHESTRATION now — the generic "workflow persona"
  // (STANDARD) kind was retired once llm-agent nodes became self-contained
  // (DEV-16943/DEV-16945). The kind toggle is gone; this page creates
  // persistent-agent (orchestration) groups only.
  const kind = 'ORCHESTRATION' as const;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [modelConfigId, setModelConfigId] = useState<string>('');
  const [hitlAllowed, setHitlAllowed] = useState(false);
  const [dashboardsAllowed, setDashboardsAllowed] = useState(false);
  const [elicitationMode, setElicitationMode] = useState<'BLOCKING' | 'NON_BLOCKING'>('BLOCKING');
  // Orchestration-only fields.
  const [requiredFacts, setRequiredFacts] = useState<RequiredFactRow[]>([]);
  const [peerDrafts, setPeerDrafts] = useState<IPeerGrantDraft[]>([]);
  const [filesystemId, setFilesystemId] = useState('');
  const [orchestrationGroupChoice, setOrchestrationGroupChoice] = useState('');
  const [newOrchestrationGroupName, setNewOrchestrationGroupName] = useState('');

  const models = useQuery({
    queryKey: ['model-configs'],
    queryFn: () => client.http.get('v1/model-configs').json<IListEnvelope<ModelConfig>>(),
  });

  // Model selection is mandatory — default to the first available config
  // rather than leaving the field empty for the user to fill in.
  useEffect(() => {
    if (modelConfigId || !models.data?.items.length) return;
    const firstModel = models.data.items[0];
    if (firstModel) setModelConfigId(firstModel.id);
  }, [modelConfigId, models.data]);

  // Existing orchestration agents — the target choices for peer grants. (A
  // grant to the agent itself can only be added after creation, once it has
  // an id.) Only fetched once the user has picked Orchestration.
  const groupsQuery = useQuery({
    queryKey: ['persistent-agents'],
    queryFn: () =>
      client.http.get('v1/persistent-agent-groups').json<IListEnvelope<PersistentAgentGroup>>(),
    enabled: kind === 'ORCHESTRATION',
  });

  const orchestrationGroupsQuery = useQuery({
    queryKey: ['orchestration-groups'],
    queryFn: () => client.http.get('v1/orchestration-groups').json<OrchestrationGroup[]>(),
    enabled: kind === 'ORCHESTRATION',
  });

  useEffect(() => {
    if (kind !== 'ORCHESTRATION' || orchestrationGroupChoice || !orchestrationGroupsQuery.data) {
      return;
    }
    // Deep-linked from a group's "Add agent" button on the Agents page —
    // only honor it if the group still exists.
    const deepLinked = search.orchestrationGroupId
      ? orchestrationGroupsQuery.data.find((g) => g.id === search.orchestrationGroupId)
      : undefined;
    if (deepLinked) {
      setOrchestrationGroupChoice(deepLinked.id);
      return;
    }
    const firstGroup = orchestrationGroupsQuery.data[0];
    if (firstGroup) setOrchestrationGroupChoice(firstGroup.id);
    else setOrchestrationGroupChoice('__new__');
  }, [kind, orchestrationGroupChoice, orchestrationGroupsQuery.data, search.orchestrationGroupId]);

  // Default new agents to an org OpenAI model when the user hasn't picked one yet.
  useEffect(() => {
    if (modelConfigId) return;
    const preferred = preferOpenAiModelId(models.data?.items);
    if (preferred) setModelConfigId(preferred);
  }, [models.data?.items, modelConfigId]);

  const filesystemsQuery = useQuery({
    queryKey: ['persistent-agent-filesystems'],
    queryFn: () =>
      client.http
        .get('v1/persistent-agent-filesystems')
        .json<IListEnvelope<{ id: string; name: string }>>(),
    enabled: kind === 'ORCHESTRATION',
  });

  const incompleteDraft = peerDrafts.some((d) => d.targetAgentId && !fromPeerGrantDraft(d));

  const create = useMutation({
    mutationFn: () => {
      return client.http
        .post('v1/persistent-agent-groups', {
          json: {
            name: name.trim(),
            description: description.trim() || undefined,
            instructions,
            modelConfigId,
            hitlAllowed,
            dashboardsAllowed,
            elicitationMode,
            ...(orchestrationGroupChoice === '__new__'
              ? { newOrchestrationGroup: { name: newOrchestrationGroupName.trim() } }
              : { orchestrationGroupId: orchestrationGroupChoice }),
            ...(filesystemId ? { filesystemId } : {}),
            requiredFacts: requiredFacts
              .filter((r) => r.laneId.trim())
              .map((r) => ({ laneId: r.laneId.trim(), type: r.type })),
            peers: peerDrafts
              .map(fromPeerGrantDraft)
              .filter((g): g is PersistentAgentPeerGrantInput => g !== null),
          },
        })
        .json<PersistentAgentGroup>();
    },
    onSuccess: async () => {
      fork(qc.invalidateQueries({ queryKey: ['persistent-agents'] }));
      fork(qc.invalidateQueries({ queryKey: ['orchestration-groups'] }));
      try {
        await nav({ to: routes.AGENTS });
      } catch (err) {
        console.error(err);
      }
    },
  });

  const groupSelectionValid =
    kind !== 'ORCHESTRATION' ||
    (orchestrationGroupChoice === '__new__'
      ? newOrchestrationGroupName.trim().length > 0
      : orchestrationGroupChoice.length > 0);
  const canSubmit =
    name.trim().length > 0 &&
    instructions.trim().length > 0 &&
    modelConfigId.length > 0 &&
    !incompleteDraft &&
    groupSelectionValid;

  return (
    <>
      <PageHeader title="Create agent" actions={<BackButton to={routes.AGENTS} label="Agents" />} />
      <div className="card" style={{ maxWidth: 720 }}>
        <div className="card-body">
          <form
            style={stack3}
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) create.mutate();
            }}
          >
            <div className="field">
              <span className="field-help">
                A long-lived, idle-by-default agent that watches over robots (or other agents) and
                wakes on events. Threads are started from it afterward on the Orchestration page.
              </span>
            </div>
            <div style={grid2}>
              <label className="field">
                <span className="field-label">Name</span>
                <input
                  required={true}
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="East Field Operator"
                />
              </label>
              <label className="field">
                <span className="field-label">Model config</span>
                <select
                  required={true}
                  className="select-native"
                  value={modelConfigId}
                  onChange={(e) => setModelConfigId(e.target.value)}
                >
                  <option value="" disabled={true}>
                    {models.isLoading ? 'Loading models…' : 'Select a model…'}
                  </option>
                  {sortModelsOpenAiFirst(models.data?.items ?? []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} ({m.provider} · {m.modelId})
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="field">
              <span className="field-label">Description (optional)</span>
              <input
                className="input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field-label">
                Instructions
                <InfoHint
                  label="How to use Handlebars in instructions"
                  text={
                    <>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Handlebars templating</div>
                      <div style={{ marginBottom: 6 }}>
                        When an <code>llm-agent</code> node has{' '}
                        <em>Interpolate node input into the agent&apos;s instructions</em> enabled,
                        your instructions are rendered as a Handlebars template against the
                        node&apos;s input before being sent as the system prompt.
                      </div>
                      <div style={{ marginBottom: 4, fontWeight: 600 }}>Common patterns</div>
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        <li>
                          <code>{'{{deviceId}}'}</code> — insert a top-level field
                        </li>
                        <li>
                          <code>{'{{site.region}}'}</code> — nested path
                        </li>
                        <li>
                          <code>{'{{#if isProd}}…{{else}}…{{/if}}'}</code> — conditionals
                        </li>
                        <li>
                          <code>{'{{#each items}}- {{this.name}}\n{{/each}}'}</code> — iterate
                          arrays
                        </li>
                      </ul>
                      <div style={{ marginTop: 6 }}>
                        Missing fields render as empty strings (no error). Output is plain text — no
                        HTML escaping is applied.
                      </div>
                    </>
                  }
                />
              </span>
              <HandlebarsInstructionsEditor
                value={instructions}
                onChange={setInstructions}
                required={true}
                rows={8}
                placeholder="You operate a single field robot. Watch its telemetry, run the assigned harvest route, and report battery/status changes."
              />
            </label>
            {kind === 'ORCHESTRATION' && (
              <>
                <label className="field">
                  <span className="field-label">Orchestration group</span>
                  <select
                    className="select-native"
                    value={orchestrationGroupChoice}
                    onChange={(e) => {
                      setOrchestrationGroupChoice(e.target.value);
                      setPeerDrafts([]);
                    }}
                  >
                    <option value="" disabled={true}>
                      {orchestrationGroupsQuery.isLoading ? 'Loading groups…' : 'Select a group…'}
                    </option>
                    {(orchestrationGroupsQuery.data ?? []).map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                        {group.isFavorite ? ' ★' : ''}
                      </option>
                    ))}
                    <option value="__new__">+ New group…</option>
                  </select>
                  <span className="field-help">
                    Agent names are unique within a group, so different groups may reuse a name.
                  </span>
                  {orchestrationGroupsQuery.error && (
                    <span className="field-help" style={{ color: 'var(--status-danger)' }}>
                      Could not load existing groups. Choose “+ New group…” to create one.
                    </span>
                  )}
                </label>
                {orchestrationGroupChoice === '__new__' && (
                  <label className="field" data-testid="new-orchestration-group-name">
                    <span className="field-label">New group name</span>
                    <input
                      className="input"
                      value={newOrchestrationGroupName}
                      onChange={(e) => setNewOrchestrationGroupName(e.target.value)}
                      placeholder="Field Operations"
                      required={true}
                    />
                  </label>
                )}
                <div className="field">
                  <span className="field-label">Required state signals</span>
                  <p className="t-sm t-muted" style={{ margin: '0 0 6px' }}>
                    State lanes every started thread must fill in (e.g. mission, robot).
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {requiredFacts.map((r, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8 }}>
                        <input
                          className="input"
                          placeholder="key (e.g. mission)"
                          value={r.laneId}
                          onChange={(e) =>
                            setRequiredFacts((rows) =>
                              rows.map((row, j) =>
                                j === i ? { ...row, laneId: e.target.value } : row,
                              ),
                            )
                          }
                          style={{ flex: 1 }}
                        />
                        <select
                          className="select-native"
                          value={r.type}
                          disabled={true}
                          style={{ flex: '0 0 120px' }}
                        >
                          <option value="text">text</option>
                        </select>
                        <button
                          className="btn btn--ghost btn--sm"
                          aria-label="remove required state signal"
                          onClick={() => setRequiredFacts((rows) => rows.filter((_, j) => j !== i))}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <div>
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={() =>
                          setRequiredFacts((rows) => [...rows, { laneId: '', type: 'text' }])
                        }
                      >
                        + Add required state signal
                      </button>
                    </div>
                  </div>
                </div>
                <label className="field">
                  <span className="field-label">Filesystem (optional)</span>
                  <select
                    className="select-native"
                    value={filesystemId}
                    onChange={(e) => setFilesystemId(e.target.value)}
                  >
                    <option value="">— none —</option>
                    {(filesystemsQuery.data?.items ?? []).map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                  </select>
                  <span className="field-help">
                    Threads get file tools (read, write, edit, list, grep) against the attached
                    filesystem. Can be changed later; filesystems can be shared by several agents.
                  </span>
                </label>
                <div className="field">
                  <span className="field-label">Peer permissions</span>
                  <p className="t-sm t-muted" style={{ margin: '0 0 6px' }}>
                    Which other persistent agents this one&apos;s threads may talk to, and how.
                    Restrictive by default — no grants means no agent-to-agent tools at all.
                    Directional: replies need a mirror grant on the peer.
                  </p>
                  <PeerGrantsEditor
                    value={peerDrafts}
                    onChange={setPeerDrafts}
                    groups={(groupsQuery.data?.items ?? [])
                      .filter((g) => g.orchestrationGroupId === orchestrationGroupChoice)
                      .map((g) => ({ id: g.id, name: g.name }))}
                  />
                  {incompleteDraft && (
                    <p className="t-xs" style={{ margin: '4px 0 0', color: 'var(--color-danger)' }}>
                      Every grant needs at least one capability enabled (and lanes/kinds filled in).
                    </p>
                  )}
                </div>
              </>
            )}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-2)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                className="checkbox"
                checked={hitlAllowed}
                onChange={(e) => setHitlAllowed(e.target.checked)}
              />
              <span className="t-sm">Allow HITL pause requests from this agent</span>
            </label>
            {kind === 'ORCHESTRATION' && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-2)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={dashboardsAllowed}
                  onChange={(e) => setDashboardsAllowed(e.target.checked)}
                />
                <span className="t-sm">Can create dashboards/widgets</span>
              </label>
            )}
            {kind === 'ORCHESTRATION' && hitlAllowed && (
              <div className="field">
                <span className="field-label">When it asks a human a question</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className={`btn btn--sm ${elicitationMode === 'BLOCKING' ? 'btn--primary' : 'btn--ghost'}`}
                    onClick={() => setElicitationMode('BLOCKING')}
                  >
                    Blocking
                  </button>
                  <button
                    type="button"
                    className={`btn btn--sm ${elicitationMode === 'NON_BLOCKING' ? 'btn--primary' : 'btn--ghost'}`}
                    onClick={() => setElicitationMode('NON_BLOCKING')}
                  >
                    Non-blocking
                  </button>
                </div>
                <span className="field-help">
                  {elicitationMode === 'BLOCKING'
                    ? 'The thread pauses until a human answers (or the ask expires).'
                    : 'The thread keeps working; the answer arrives later as a notification.'}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              <button
                type="submit"
                className="btn btn--primary"
                disabled={create.isPending || !canSubmit}
              >
                {create.isPending ? 'Creating…' : 'Create'}
              </button>
              {create.error ? (
                <span
                  className="t-xs"
                  style={{ color: 'var(--status-danger)', alignSelf: 'center' }}
                >
                  {(create.error as Error).message}
                </span>
              ) : null}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
